import { GridLayoutItem } from "@progress/kendo-react-layout";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConditionBuilder } from "@/components/ConditionBuilder/hooks/useConditionBuilder";
import {
  ConditionAttributeOption,
  ConditionOption,
  MatchWithOption,
} from "@/components/ConditionBuilder/types";
import { FormDropDownList } from "@/components/Forms";

interface ConditionMatchWithFieldProps {
  fieldName: string;
  attributeFieldName: string;
  operatorFieldName: string;
  valueFieldName: string;
}

const ConditionMatchWithField: React.FC<ConditionMatchWithFieldProps> = ({
  fieldName,
  attributeFieldName,
  operatorFieldName,
  valueFieldName,
}) => {
  const { t } = useTranslation();
  const { valueGetter, onChange, config } = useConditionBuilder();
  const methods = useFormContext();

  const attribute = valueGetter(attributeFieldName) as
    | ConditionAttributeOption
    | undefined;
  const operator = valueGetter(operatorFieldName) as
    | ConditionOption
    | undefined;

  // Shared queries — IDENTICAL keys to ConditionOperatorField /
  // ConditionValueField so react-query serves a cache hit (no extra calls).
  const { data: resolvedMeta, isFetching: isAttrMetaFetching } = useQuery({
    queryKey: ["resolveAttribute", attribute?.id, attribute?.type],
    queryFn: () => config.resolveAttribute!(attribute!.id, attribute),
    enabled: !!attribute && !!config.resolveAttribute,
  });
  const { data: resolvedOperatorMeta, isFetching: isOperatorMetaFetching } =
    useQuery({
      queryKey: [
        "resolveOperator",
        attribute?.id,
        attribute?.type,
        operator?.id,
      ],
      queryFn: () =>
        config.resolveOperator!(attribute!.id, operator!.id, attribute),
      enabled: !!attribute && !!operator && !!config.resolveOperator,
    });

  // Priority: resolveOperator > resolveAttribute > per-attribute static.
  const matchWithOptions: MatchWithOption[] = useMemo(
    () =>
      resolvedOperatorMeta?.matchWithOptions ??
      resolvedMeta?.matchWithOptions ??
      attribute?.matchWithOptions ??
      [],
    [
      resolvedOperatorMeta?.matchWithOptions,
      resolvedMeta?.matchWithOptions,
      attribute?.matchWithOptions,
    ],
  );

  // Gate on operator?.id: the field is disabled until an operator is selected,
  // so the validator must not flag a disabled field as required. Without this
  // gate an attribute carrying static matchWithOptions would block submission
  // before the user can interact with the field.
  const hasOptions = !!operator?.id && matchWithOptions.length > 0;

  // Required ONLY when options exist for this row. Rows whose attribute/operator
  // yield no Match With options must NOT be blocked from submission.
  const matchWithValidator = useCallback(
    (value: unknown) => {
      if (!hasOptions) return undefined;
      const option = value as MatchWithOption | undefined;
      return !option?.id ? t("validation.required") : undefined;
    },
    [t, hasOptions],
  );

  // Belt-and-suspenders registration: same rationale as ConditionOperatorField.
  // useController inside FormDropDownList registers the validate rule, but for
  // deeply-nested array paths RHF has been observed to drop the rule from
  // re-validation tracking — so register here to keep _fields[name]._f.validate
  // populated.
  useEffect(() => {
    methods.register(fieldName as never, {
      validate: (v: unknown) => {
        const msg = matchWithValidator(v);
        return typeof msg === "string" && msg.length > 0 ? msg : true;
      },
    });
  }, [methods, fieldName, matchWithValidator]);

  // Force re-validation on change. Use methods.watch (callback form) rather than
  // useWatch to avoid an infinite loop (useWatch returns a fresh deep clone every
  // render).
  useEffect(() => {
    const subscription = methods.watch((_values, info) => {
      if (info?.name === fieldName && info?.type === "change") {
        void methods.trigger(fieldName as never);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, fieldName]);

  // Reset own value when the selected attribute changes so a stale Match With
  // from a previous attribute is never submitted.
  const prevAttributeId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentId = attribute?.id;
    if (
      prevAttributeId.current !== undefined &&
      currentId !== undefined &&
      prevAttributeId.current !== currentId
    ) {
      onChange(fieldName, { value: null });
    }
    prevAttributeId.current = currentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attribute?.id]);

  // Reset own value when the selected operator changes — the available Match
  // With options depend on the operator.
  const prevOperatorId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentId = operator?.id;
    if (
      prevOperatorId.current !== undefined &&
      currentId !== undefined &&
      prevOperatorId.current !== currentId
    ) {
      onChange(fieldName, { value: null });
    }
    prevOperatorId.current = currentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator?.id]);

  // Clear a stale selection that the option sources no longer offer. The
  // attribute/operator-change resets above already cover input changes; this
  // handles the remaining case where an async refetch empties or changes the
  // option list for the SAME (attribute, operator). Guarded on optionsSettled so
  // a value that is merely still loading (e.g. a seeded edit-mode selection) is
  // never wiped — disabled queries report isFetching=false, so they count as
  // settled.
  const optionsSettled = !isAttrMetaFetching && !isOperatorMetaFetching;
  const selectedMatchWithId = (
    valueGetter(fieldName) as MatchWithOption | null | undefined
  )?.id;
  const selectionStillValid =
    !selectedMatchWithId ||
    matchWithOptions.some(o => o.id === selectedMatchWithId);
  useEffect(() => {
    if (!config.enableMatchWith || !selectedMatchWithId) return;
    if (!optionsSettled || selectionStillValid) return;
    if (!matchWithOptions.length) return; // wait till options settle
    onChange(fieldName, { value: null });
  }, [
    config.enableMatchWith,
    selectedMatchWithId,
    optionsSettled,
    selectionStillValid,
    fieldName,
    matchWithOptions,
    onChange,
  ]);

  // Custom selection handler that clears the sibling value field when matchWith
  // changes, preventing type mismatch crashes when switching between Match With
  // types with incompatible value types (e.g., text → multi-select).
  const handleMatchWithSelection = useCallback(() => {
    // Clear the value field synchronously
    onChange(valueFieldName, { value: null });
  }, [valueFieldName, onChange]);

  return (
    <GridLayoutItem>
      <FormDropDownList
        name={fieldName}
        data={matchWithOptions}
        label={config.labels?.matchWith ?? "Match With"}
        floatingLabel={true}
        disabled={
          config.readOnly || !attribute?.id || !operator?.id || !hasOptions
        }
        disableAutoClear={true}
        validator={matchWithValidator}
        onSelectionChange={handleMatchWithSelection}
      />
    </GridLayoutItem>
  );
};

export default ConditionMatchWithField;
